import React, { HTMLAttributes } from 'react';
import { FormikConfig, FormikHelpers, FormikValues } from 'formik';
import { UnitCardTypes } from '../../../../components/unitDetailsCard/UnitCardTypes';
import { AccountPanelTypes } from '../../../account';

export namespace DynamicFormTypes {
  export type TFormData = {
    label: string;
    data: any;
    preFix?: string;
  };

  export type TPaymentRequest = {
    callBackHandler: AccountPanelTypes.TCallBackHandler | undefined;
    public_key: string;
    payment_method: any;
    cardProcessorIds: any;
    configs: {
      total_amount: string;
      reservationConfigs: TReservationConfig;
      moveInConfigs: TMoveInConfig;
    };
    facilityId: string;
    tenantId: string;
    tenantIdFromCreationAPI: string;
    individualUnitData: any;
    contextData: any;
  };
  export type TSchema = {
    label: string;
    required: boolean;
    type: string;
    isSuggested: boolean;
    options?: [];
    values?: [];
  };

  export type TFormField = {
    label: string;
    type: string;
    required: boolean;
    isSuggested: boolean;
  };

  export type TData = {
    heading: string;
    data: TFormField[];
  };

  export type TSection = {
    label: string;
    data: any;
    id: number;
    preFix?: string;
  };

  export interface IDynamicForm extends HTMLAttributes<HTMLDivElement> {
    response: any;
    unitsData: any;
    unitCardsConfig: any;
    bodyHeight: number;
    section: TSection[];
    individualUnitDataContext: any;
    onSetIndividualData: React.Dispatch<any> | any;
    facilityID: string;
    path: string;
    gatsbyPathName: string;
    ledgerInfo: any;
    tenantId: string;
    formInitialValues: any;
    locationDetails: any;
    stepperFormLabel?: string;
    globalContext?: any;
    sessionTimerContext?: any;
    facilityContext: any;
    ledgerIdFromReservation?: string;
    reservationId?: string;
    getCurrentLocation?: any;
    promoPlans?: any;
  }

  export interface IGetForm extends HTMLAttributes<HTMLDivElement> {
    getFormData: TFormData;
  }

  export interface IGenericForm extends HTMLAttributes<HTMLDivElement> {
    genericFormData: TFormData;
    unitsData: any;
    bodyHeight: number;
    unitCardsConfig: UnitCardTypes.TUnitCardsConfig;
    individualUnitDataContext: any;
    onSetIndividualData: React.Dispatch<any>;
    paymentComponentReq?: any;
    facilityId: string;
    tenantIdFromCreationAPI: string;
    ledgerId: string;
    facilityContext: any;
    noPayReservationCallCount: number;
    setNoPayReservationCallCount: React.Dispatch<React.SetStateAction<number>>;
    tenantToken: string | undefined;
  }

  export type TConfirmationSectionType =
    | {
        tenantInformation: any;
        moveInDetails: any;
        tenantAlternateInformation?: undefined;
        tenantDriversLicenseInfo?: undefined;
        militaryInformation?: undefined;
      }
    | {
        tenantInformation: any;
        tenantAlternateInformation: any;
        tenantDriversLicenseInfo: any;
        militaryInformation: any;
        moveInDetails: any;
      };
  export type TAmountAndLedgerId = {
    total_amount: any;
    ledgerIdFromSMS: string;
  };
  export type TMoveInPaymentConfig = TAmountAndLedgerId & {
    move_in: {
      unit_type_id: string | number;
      rental_info: any;
    };
    reserve?: undefined;
  };
  export type TReservationPaymentConfig = TAmountAndLedgerId & {
    reserve: {
      reservation_date: string;
      unit_type_id: string | number;
      rental_info: any;
    };
    move_in?: undefined;
  };
  export type TPaymentConfig =
    | TMoveInPaymentConfig
    | TReservationPaymentConfig
    | {};
  export type TTenantAddress = {
    'Address Line 1': TFormField;
    'Address Line 2': TFormField;
    City: TFormField;
    'State/Province': TFormField;
  };
  export type TFormItem = {
    0: TFormField;
    tenantAddress: TFormField[];
  };
  export type TFields = {
    key: string;
    label: string;
    value: string;
  };
  export type TSectionData = {
    title?: string;
    fields?: TFields[];
  };
  export type TTenantInformation = {
    Name: string;
    Email: string;
    'Phone Number': string;
    Address: string;
    'Date of Birth': string;
  };
  export type TDLicenseInfo = {
    'License Number': string;
    'License Expiration Date': string;
  };
  export type TMilitaryInformation = {
    'Military Type': string;
    'Military Branch': string;
    'Military ID': string;
    'Military Deployed Until': string;
    'Military Division': string;
    'Military Commanding Officer': string;
    'Military C.O Phone': string;
  };
  export type TMoveInDetails = {
    'Move-In Date': string;
    'Billing Cycle': string;
  };
  export type TUpdatedFormData = {
    tenantInformation: TTenantInformation;
    tenantAlternateInformation: TTenantInformation;
    tenantDriversLicenseInfo: TDLicenseInfo;
    militaryInformation: TMilitaryInformation;
    moveInDetails: TMoveInDetails;
  };
  export type TConfirmationConfig =
    | {
        tenantInformation: any;
        moveInDetails: any;
        tenantAlternateInformation?: undefined;
        tenantDriversLicenseInfo?: undefined;
        militaryInformation?: undefined;
      }
    | {
        tenantInformation: any;
        tenantAlternateInformation: any;
        tenantDriversLicenseInfo: any;
        militaryInformation: any;
        moveInDetails: any;
      }
    | undefined;

  export interface FormikStepProps
    extends Pick<FormikConfig<FormikValues>, 'children' | 'validationSchema'> {
    label: string;
    stepperFormLabel?: string;
  }

  export type TFormikStepper = {
    onSubmit: (
      values: FormikValues,
      helpers: FormikHelpers<FormikValues>
    ) => void;

    children:
      | React.ReactNode
      | ((props: FormikConfig<FormikValues>) => React.ReactNode);
  };

  export type TMoveInReservationHelper = {
    setShowError: React.Dispatch<React.SetStateAction<boolean>>;
    TenantInfo_DriverLicenseNumber: any;
    tenantCreateRequest: any;
    errors: any[];
    setErrors: React.Dispatch<React.SetStateAction<any[]>>;
    individualUnitDataContext: any;
    TenantInfo_DesiredMoveInDate: any;
    gatsbyPathName: string;
    setPaymentComponentReq: React.Dispatch<any>;
    tenantUserName: any;
    tenantUserPassword: any;
    facilityID: string;
    ledgerInfo: any;
    pathName: string;
    tenantId: string;
    values: any;
    globalContext: any;
    sessionTimerContext: any;
    setIsAccountCreated: any;
    ledgerIdFromReservation?: string;
    reservationId?: string;
    facilityContext: any;
  };
  export type PaymentConfig = {
    total_amount: string | number;
    isReservation: boolean;
    isMoveIn: boolean;
    data: {
      ledger_id: string;
    };
  };
  export type TReservationConfig = PaymentConfig & {
    url: {
      reservation: string;
    };
    reservation: any;
  };

  export type TMoveInConfig = PaymentConfig & {
    url: {
      movein: string;
    };
    move_in: any;
  };
  export type TPaymentComponentTypes = {
    callBackHandler: AccountPanelTypes.TCallBackHandler;
    billingDetailsConfig?: AccountPanelTypes.TBillingDetailsInfoConfig;
    public_key: string;
    payment_method: any;
    cardProcessorIds: any;
    configs?: {
      total_amount: string | number;
      reservationConfigs: TReservationConfig;
      moveInConfigs: TMoveInConfig;
    };
    facilityId?: string;
    tenantId?: string;
    tenantIdFromCreationAPI?: string;
    individualUnitData?: any;
  };
  export type TSelectOption = {
    value: string | number | readonly string[] | undefined;
    key: any;
  };
}
